import { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2 } from "lucide-react";

/**
 * Voice note recorder using MediaRecorder.
 * Props:
 *  - onRecorded(blob, durationSec)
 *  - disabled
 */
export default function VoiceRecorder({ onRecorded, disabled }) {
    const [state, setState] = useState("idle"); // idle | recording | denied
    const [seconds, setSeconds] = useState(0);
    const mediaRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const tickRef = useRef(null);
    const startedAtRef = useRef(0);

    useEffect(() => () => stopAll(), []);

    const stopAll = () => {
        try { if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop(); } catch (_e) {}
        try { streamRef.current?.getTracks()?.forEach((t) => t.stop()); } catch (_e) {}
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    };

    const start = async () => {
        if (disabled) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
                : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
            const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
            mediaRef.current = mr;
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = () => {
                const dur = (Date.now() - startedAtRef.current) / 1000;
                const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
                if (blob.size > 0) onRecorded(blob, dur);
                stopAll();
                setState("idle"); setSeconds(0);
            };
            startedAtRef.current = Date.now();
            mr.start();
            setState("recording");
            tickRef.current = setInterval(() => {
                setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
            }, 250);
        } catch (_e) {
            setState("denied");
        }
    };

    const stop = () => {
        try { mediaRef.current?.stop(); } catch (_e) {}
    };

    const cancel = () => {
        try {
            if (mediaRef.current) mediaRef.current.ondataavailable = null;
            mediaRef.current?.stop();
        } catch (_e) {}
        chunksRef.current = [];
        stopAll();
        setState("idle"); setSeconds(0);
    };

    if (state === "denied") {
        return <span className="text-xs text-[var(--danger)] font-mono">mic blocked</span>;
    }

    if (state === "recording") {
        const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
        const ss = String(seconds % 60).padStart(2, "0");
        return (
            <div className="flex items-center gap-2" data-testid="voice-recording">
                <span className="inline-flex items-center gap-1.5 text-[var(--danger)] font-mono text-xs">
                    <span className="w-2 h-2 rounded-full bg-[var(--danger)] animate-pulse" /> REC {mm}:{ss}
                </span>
                <button type="button" onClick={stop} title="Stop & send" className="p-2 border border-[var(--gold)] text-[var(--gold)]" data-testid="voice-stop">
                    <Square size={12} />
                </button>
                <button type="button" onClick={cancel} title="Cancel" className="p-2 border border-[var(--border-bright)] text-[var(--text-muted)] hover:text-[var(--danger)]" data-testid="voice-cancel">
                    <Trash2 size={12} />
                </button>
            </div>
        );
    }

    return (
        <button type="button" onClick={start} disabled={disabled} title="Record voice note"
            className="p-2 border border-[var(--border-bright)] text-[var(--text-muted)] hover:text-[var(--gold)] hover:border-[var(--gold)]"
            data-testid="voice-start">
            <Mic size={14} />
        </button>
    );
}
